/**
 * App-wide display configuration: block-explorer links (per blockchain),
 * chain-avatar logos, and hash/address truncation lengths.
 *
 * All are configured in ONE place here and applied to the shared
 * `@authe/fe-commons` registries at bootstrap, so every surface — the
 * transaction table, the graph, address/hash chips, drill-downs — opens the
 * same explorer for a given chain, renders the same seeded chain logo, and
 * middle-truncates values identically.
 *
 * Chain logos come from the CDN bucket the backend seeder (`authe-seed logos`)
 * mirrors icons to; installing the resolver here lets every `ChainAvatar` pick
 * up its logo without threading a `logoResolver` prop through the tree. Chains
 * outside the seeded (CCIP) set — and any logo that 404s — fall back to the
 * brand-tinted native-token initials chip.
 *
 * Truncation is purely cosmetic: the full hash/address is always kept for
 * copy + search, so a truncated table cell stays searchable by its complete
 * value.
 *
 * Ops overrides (no code change needed):
 *  - `VITE_EXPLORER_OVERRIDES` — JSON map of chain id → `{ name?, baseUrl?, paths? }`
 *    e.g. `{"gnosis":{"name":"Blockscout","baseUrl":"https://gnosis.blockscout.com"}}`
 *  - `VITE_EXPLORER_PATHS` — JSON map of default path segments per link type
 *    e.g. `{"tx":"transaction"}` (for non-Etherscan-scheme explorers)
 *  - `VITE_ASSET_CDN_BASE` — CDN origin the seeded chain logos are served from
 *    (defaults to the app's `cdnUrl`, i.e. the same CloudFront origin as the
 *    transaction-body CDN)
 *  - `VITE_HASH_TRUNCATION` / `VITE_ADDRESS_TRUNCATION` — `"head,tail"` (e.g. `"10,8"`):
 *    characters of the identifier kept at each end, a `0x` prefix not counted
 */
import { readEnvKey } from "@authe/fe-commons/env";
import { configureExplorers, type ExplorerConfig, type ExplorerLinkType } from "@authe/fe-commons/chains";
import { configureChainLogos } from "@authe/fe-commons/assets";
import { configureTruncation, DEFAULT_TRUNCATION, type TruncationLengths } from "@authe/fe-commons/display";
import { assetCdnBase, envSource } from "./env";

/**
 * Built-in per-chain explorer overrides. The chain registry already seeds the
 * canonical explorer for every chain; add an entry here only to point a chain
 * at a different explorer or remap its path segments. Env (`VITE_EXPLORER_*`)
 * is layered on top of this at bootstrap.
 */
export const EXPLORER_OVERRIDES: Record<string, ExplorerConfig> = {
  // example: gnosis: { name: "Blockscout", baseUrl: "https://gnosis.blockscout.com" },
};

function parseJsonEnv<T>(key: string): T | undefined {
  const raw = readEnvKey(envSource, key);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    if (import.meta.env.DEV) {
      console.warn(`[config:display] ignoring invalid JSON in VITE_${key}`);
    }
    return undefined;
  }
}

/** Parse a `"head,tail"` env value into truncation lengths. */
function parseTruncationEnv(key: string): Partial<TruncationLengths> | undefined {
  const raw = readEnvKey(envSource, key);
  if (!raw) return undefined;
  const [head, tail] = raw.split(",").map((n) => Number.parseInt(n.trim(), 10));
  const out: Partial<TruncationLengths> = {};
  if (Number.isFinite(head)) out.head = head;
  if (Number.isFinite(tail)) out.tail = tail;
  return Object.keys(out).length ? out : undefined;
}

/**
 * Apply the dashboard's display configuration to the shared registries. Safe to
 * call once at startup (before render). Env overrides win over the built-ins.
 */
export function configureDisplayDefaults(): void {
  const explorerChains: Record<string, ExplorerConfig> = {
    ...EXPLORER_OVERRIDES,
    ...parseJsonEnv<Record<string, ExplorerConfig>>("EXPLORER_OVERRIDES"),
  };
  const explorerPaths = parseJsonEnv<Partial<Record<ExplorerLinkType, string>>>("EXPLORER_PATHS");
  configureExplorers({
    chains: explorerChains,
    ...(explorerPaths ? { paths: explorerPaths } : {}),
  });

  // Point every `ChainAvatar` at the seeded logo bucket (same CloudFront origin
  // as the tx-body CDN unless overridden). Defaults to the CCIP allowlist, so
  // unseeded chains never fire a pointless 404 and just show initials.
  configureChainLogos({
    cdnBase: assetCdnBase,
  });

  // The platform default (which the backend's titles mirror) unless ops override it.
  configureTruncation({
    hash: { ...DEFAULT_TRUNCATION.hash, ...parseTruncationEnv("HASH_TRUNCATION") },
    address: { ...DEFAULT_TRUNCATION.address, ...parseTruncationEnv("ADDRESS_TRUNCATION") },
  });
}
